/**
 * App-side wiring for `@heidi/ads/data`.
 *
 * Injects the app's shared QueryClient, the reactive auth-token hook, the
 * notifications query the inmail sync reads, and the cached audience signals a
 * placement reads before asking the backend for a creative. Transport is not
 * injected: `data/hcp-verification` reads the `@heidi/api/client` registry,
 * which the `scribeHTTP` side-effect import below populates first.
 *
 * The audience signals mirror the server's audience check on `GET /placements`
 * (`advertisements/availability.py`): account country, managed status, team
 * membership and conceptual tier, plus the one deployment fact the build knows
 * — on-prem never holds ad data. The server stays the decision; the hint only
 * skips a round trip whose answer is already in a cache. Each signal is
 * `undefined` until its cache has loaded, which the package treats as "ask".
 *
 * Every read is an observer on a query the auth shell already keeps mounted
 * (user, joined teams, billing), so a placement adds a subscription and no
 * request. Widening a server rule — a new country, a wider audience — must
 * land here too, or the client keeps withholding a request the server would
 * fill; narrowing one is safe.
 *
 * Imported at module scope in `_app.tsx` after the commerce wiring, which
 * binds the billing reader the tier signal goes through.
 */
// Side-effect: builds the scribe clients AND sets the @heidi/api registry.
import '@/utils/scribeHTTP';

import { useQueryNotifications } from '@/hooks/query/useQueryNotifications';
import { useAuthToken } from '@/hooks/useAuthToken';
import { isOnPrem } from '@/utils/buildProfile';
import { reactQueryClient } from '@/utils/reactQueryClient';
import {
  type AdAudienceSignals,
  ADS_SUPPORTED_COUNTRY,
  configureAdsData,
} from '@heidi/ads/data';
import { useUserBillingInfo } from '@heidi/commerce/data';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useQueryJoinedTeams, useQueryUser, useUser } from '@heidi/workspace/data';

/** Exported for the wiring test; a placement reaches it through the seam. */
export const useAdAudienceSignals = (): AdAudienceSignals => {
  const { data: isInAdCountry } = useUser((user) =>
    user === undefined ? undefined : user.country === ADS_SUPPORTED_COUNTRY
  );
  const { data: isManaged } = useUser((user) => user?.is_managed);
  const joinedTeamCount = useQueryJoinedTeams().data?.joined_teams.length ?? 0;
  const tier = useUserBillingInfo().data?.new_product_tier;

  return {
    isOnPremBuild: isOnPrem,
    isInAdCountry,
    isManaged,
    // An empty list and an unloaded one look alike here, so only a non-empty
    // list is a verdict.
    isInTeam: joinedTeamCount > 0 ? true : undefined,
    isFreeTier: tier === undefined ? undefined : tier === SubscriptionTier.FREE,
  };
};

configureAdsData({
  useAuthToken,
  queryClient: reactQueryClient,
  useQueryNotifications,
  useQueryUser,
  useAudienceSignals: useAdAudienceSignals,
});
